// Clone Permissions Modal Component
// Allows cloning permissions from another team with change preview

import { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Search, AlertTriangle, Plus, Minus } from 'lucide-react';
import type { Team, Permission } from '@/types/teams';

interface ClonePermissionsModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (sourceTeamId: string) => void;
  currentTeam: Team;
  teams: Team[];
  permissions: Permission[];
  currentPermissions: Record<string, string[]>; // Current permission state
}

interface PermissionChange {
  permissionId: string;
  permissionName: string;
  subPermissionId: string;
  subPermissionName: string;
  changeType: 'add' | 'remove';
}

export function ClonePermissionsModal({
  open,
  onClose,
  onConfirm,
  currentTeam,
  teams,
  permissions,
  currentPermissions,
}: Readonly<ClonePermissionsModalProps>) {
  const [selectedTeamId, setSelectedTeamId] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');

  // Filter out current team from the list
  const availableTeams = teams.filter(t => t.id !== currentTeam.id);

  // Calculate permission changes
  const permissionChanges = useMemo((): PermissionChange[] => {
    if (!selectedTeamId) return [];

    const sourceTeam = teams.find(t => t.id === selectedTeamId);
    if (!sourceTeam) return [];

    const changes: PermissionChange[] = [];

    // Get source team's granted permissions
    const sourcePermissions = new Set<string>();
    sourceTeam.permissions?.forEach(teamPerm => {
      if (teamPerm.granted) {
        teamPerm.subPermissions?.forEach(sp => {
          if (sp.granted) {
            sourcePermissions.add(`${teamPerm.permissionId}:${sp.key}`);
          }
        });
      }
    });

    // Get current team's granted permissions
    const currentGranted = new Set<string>();
    Object.entries(currentPermissions).forEach(([permId, subPermIds]) => {
      subPermIds.forEach(subPermId => {
        currentGranted.add(`${permId}:${subPermId}`);
      });
    });

    // Find additions (in source but not in current)
    sourcePermissions.forEach(key => {
      if (!currentGranted.has(key)) {
        const [permId, subPermKey] = key.split(':');
        const permission = permissions.find(p => p.id === permId);
        const subPermission = permission?.subPermissions?.find(sp => sp.key === subPermKey);
        
        if (permission && subPermission) {
          changes.push({
            permissionId: permId,
            permissionName: permission.displayName ?? '',
            subPermissionId: subPermKey,
            subPermissionName: subPermission.displayName ?? '',
            changeType: 'add',
          });
        }
      }
    });

    // Find removals (in current but not in source)
    currentGranted.forEach(key => {
      if (!sourcePermissions.has(key)) {
        const [permId, subPermKey] = key.split(':');
        const permission = permissions.find(p => p.id === permId);
        const subPermission = permission?.subPermissions?.find(sp => sp.key === subPermKey);
        
        if (permission && subPermission) {
          changes.push({
            permissionId: permId,
            permissionName: permission.displayName ?? '',
            subPermissionId: subPermKey,
            subPermissionName: subPermission.displayName ?? '',
            changeType: 'remove',
          });
        }
      }
    });

    return changes;
  }, [selectedTeamId, teams, permissions, currentPermissions]);

  // Filter changes by search
  const filteredChanges = useMemo(() => {
    if (!searchQuery) return permissionChanges;
    
    const query = searchQuery.toLowerCase();
    return permissionChanges.filter(change =>
      change.permissionName?.toLowerCase().includes(query) ||
      change.subPermissionName?.toLowerCase().includes(query)
    );
  }, [permissionChanges, searchQuery]);

  const additionsCount = permissionChanges.filter(c => c.changeType === 'add').length;
  const removalsCount = permissionChanges.filter(c => c.changeType === 'remove').length;

  const handleConfirm = () => {
    if (selectedTeamId) {
      onConfirm(selectedTeamId);
      onClose();
    }
  };

  const handleClose = () => {
    setSelectedTeamId('');
    setSearchQuery('');
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>Clone Permissions from Team</DialogTitle>
          <DialogDescription>
            Select a team to copy its permissions. This will override your current permissions.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Team Selection */}
          <div className="space-y-2">
            <Label>Select Team</Label>
            <Select value={selectedTeamId} onValueChange={setSelectedTeamId}>
              <SelectTrigger>
                <SelectValue placeholder="Choose a team to clone from..." />
              </SelectTrigger>
              <SelectContent>
                {availableTeams.map(team => (
                  <SelectItem key={team.id} value={team.id}>
                    <div className="flex items-center gap-2">
                      <div
                        className="h-4 w-4 rounded"
                        style={{ backgroundColor: team.color }}
                      />
                      <span>{team.name}</span>
                      {team.isDefault && (
                        <Badge variant="outline" className="ml-2">Default</Badge>
                      )}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Warning */}
          {selectedTeamId && permissionChanges.length > 0 && (
            <div className="flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900 rounded-lg">
              <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-500 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-amber-900 dark:text-amber-100">
                  This will modify your current permissions
                </p>
                <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                  {additionsCount > 0 && `${additionsCount} permission(s) will be added`}
                  {additionsCount > 0 && removalsCount > 0 && ' and '}
                  {removalsCount > 0 && `${removalsCount} permission(s) will be removed`}
                  . Changes will not be saved until you click "Save Changes".
                </p>
              </div>
            </div>
          )}

          {/* Permission Changes Preview */}
          {selectedTeamId && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Permission Changes ({permissionChanges.length})</Label>
                {permissionChanges.length > 0 && (
                  <div className="relative flex-1 max-w-xs">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search changes..."
                      className="pl-9 h-9"
                    />
                  </div>
                )}
              </div>

              {permissionChanges.length === 0 ? (
                <div className="flex items-center justify-center h-32 border rounded-lg bg-muted/50">
                  <p className="text-sm text-muted-foreground">
                    No changes - permissions are identical
                  </p>
                </div>
              ) : (
                <ScrollArea className="h-[300px] border rounded-lg">
                  <div className="p-4 space-y-2">
                    {filteredChanges.length === 0 ? (
                      <div className="flex items-center justify-center h-32">
                        <p className="text-sm text-muted-foreground">
                          No changes match your search
                        </p>
                      </div>
                    ) : (
                      filteredChanges.map((change, index) => (
                        <div
                          key={`${change.permissionId}-${change.subPermissionId}-${index}`}
                          className="flex items-center gap-3 p-3 rounded-lg border bg-card"
                        >
                          {change.changeType === 'add' ? (
                            <div className="flex items-center justify-center h-8 w-8 rounded-full bg-green-100 dark:bg-green-950 shrink-0">
                              <Plus className="h-4 w-4 text-green-600 dark:text-green-400" />
                            </div>
                          ) : (
                            <div className="flex items-center justify-center h-8 w-8 rounded-full bg-red-100 dark:bg-red-950 shrink-0">
                              <Minus className="h-4 w-4 text-red-600 dark:text-red-400" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">
                              {change.permissionName}
                            </p>
                            <p className="text-xs text-muted-foreground truncate">
                              {change.subPermissionName}
                            </p>
                          </div>
                          <Badge
                            variant={change.changeType === 'add' ? 'default' : 'destructive'}
                            className="shrink-0"
                          >
                            {change.changeType === 'add' ? 'Add' : 'Remove'}
                          </Badge>
                        </div>
                      ))
                    )}
                  </div>
                </ScrollArea>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!selectedTeamId || permissionChanges.length === 0}
          >
            Apply Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
